// Markdown with foldable sections, for long agent output.
//
// Splitting is recursive on the SHALLOWEST heading level present, so a fold takes
// its deeper subsections with it. Bodies render through the shared <Markdown> (no
// rehype-raw), so model output cannot inject HTML.
import { type ReactNode, useState } from "react";
import type { Components } from "react-markdown";

import { DECISION_MARKER, EMAIL_MARKER } from "../utils/emailMarker";
import { Markdown } from "./Markdown";
import { isTextSelectionClick, SELECTABLE_LABEL_STYLE } from "./selectableButton";
import styles from "./CollapsibleMarkdown.module.css";
import { Chevron } from "./Chevron";

interface Section {
  level: number;
  title: string;
  body: string;
}

const HEADING_RE = /^(#{1,6})\s+(.+?)\s*#*\s*$/;

/** Where the answer places an email, the card for it; null renders nothing. */
export type RenderEmail = (marker: string) => ReactNode;

// split() with a capture group alternates prose and the marker.
const Body = ({
  text,
  components,
  transform,
  email,
}: {
  text: string;
  components?: Components;
  transform: (body: string) => string;
  email: RenderEmail;
}) => (
  <>
    {/* A decision is a scheduled pass's card; nowhere this renders has one to show. */}
    {text.replace(DECISION_MARKER, "").split(EMAIL_MARKER).map((part, i) =>
      i % 2 === 1 ? (
        <div key={i}>{email(part)}</div>
      ) : part.trim() ? (
        <Markdown key={i} components={components}>
          {transform(part)}
        </Markdown>
      ) : null,
    )}
  </>
);
const FENCE_RE = /^ {0,3}(```+|~~~+)/;

const splitSections = (
  markdown: string,
): { preamble: string; sections: Section[] } => {
  const lines = markdown.split("\n");
  const headings: { at: number; level: number; title: string }[] = [];
  // Fenced code is skipped so a shell comment ("# rebuild the index") inside a
  // block never becomes a section heading.
  let fence: string | null = null;
  for (let i = 0; i < lines.length; i += 1) {
    const fenceMatch = FENCE_RE.exec(lines[i]);
    if (fenceMatch) {
      if (fence === null) fence = fenceMatch[1][0];
      else if (lines[i].trimStart().startsWith(fence)) fence = null;
      continue;
    }
    if (fence !== null) continue;
    const heading = HEADING_RE.exec(lines[i]);
    if (heading) {
      headings.push({ at: i, level: heading[1].length, title: heading[2] });
    }
  }
  if (headings.length === 0) return { preamble: markdown, sections: [] };
  const topLevel = Math.min(...headings.map((h) => h.level));
  const tops = headings.filter((h) => h.level === topLevel);
  return {
    preamble: lines.slice(0, tops[0].at).join("\n"),
    sections: tops.map((h, i) => ({
      level: h.level,
      title: h.title,
      body: lines
        .slice(h.at + 1, i + 1 < tops.length ? tops[i + 1].at : lines.length)
        .join("\n"),
    })),
  };
};

// A <button> for keyboard access, so it needs the selectable-label style and the
// drag-to-select guard to let the title be selected without folding the section.
const SectionBlock = ({
  section,
  components,
  transform,
  email,
}: {
  section: Section;
  components?: Components;
  transform: (body: string) => string;
  email: RenderEmail;
}) => {
  const [open, setOpen] = useState(true);
  return (
    <section className={styles.section}>
      <button
        type="button"
        className={styles.header}
        style={SELECTABLE_LABEL_STYLE}
        aria-expanded={open}
        onClick={(e) => {
          if (isTextSelectionClick(e)) return;
          setOpen(!open);
        }}
      >
        <span className={styles.caret} aria-hidden="true">
          <Chevron tree open={open} />
        </span>
        <span className={styles.title} data-level={section.level}>
          {section.title}
        </span>
      </button>
      {open && (
        <div className={styles.body}>
          <CollapsibleMarkdown components={components} transform={transform} email={email}>
            {section.body}
          </CollapsibleMarkdown>
        </div>
      )}
    </section>
  );
};

export const CollapsibleMarkdown = ({
  children,
  components,
  transform,
  email,
}: {
  children: string;
  // Citation rendering, when the caller has it. Both travel together: the
  // transform rewrites `[upload_id "..."]` into links only these components know
  // how to render, so passing one without the other shows raw hrefs.
  components?: Components;
  transform?: (body: string) => string;
  email: RenderEmail;
}) => {
  const render = transform ?? ((body: string) => body);
  const { preamble, sections } = splitSections(children);
  if (sections.length === 0) {
    return <Body text={children} components={components} transform={render} email={email} />;
  }
  return (
    <div className={styles.root}>
      {preamble.trim() !== "" && (
        <Body text={preamble} components={components} transform={render} email={email} />
      )}
      {sections.map((section, i) => (
        <SectionBlock
          key={`${i}-${section.title}`}
          section={section}
          components={components}
          transform={render}
          email={email}
        />
      ))}
    </div>
  );
};
